/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { HttpResponse } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { AppService } from '@services/app/app.service';
import { UserDataDTO, AuthenticateDTO } from '@api/models';
import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export const AUTH_HEADER_NAME = 'qinetic_authorization';

export const AUTH_STORAGE_ITEM_NAME = 'requestToken';

/**
 * L'app utilizza un sitema di autenticazione tramite cookie http-only.
 * Solo nell'ambiente di sviluppo viene utilizzato il classico sistema
 * tramite l'header qinetic_authorization e requestToken
 */

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  constructor(private apiService: ApiService, private appService: AppService) {}

  /**
   * autentica/verifica l'user
   */
  authenticate(code: string): Observable<void> {
    const req: AuthenticateDTO = {
      'uuid-info': code,
      'application-code': 'CQS'
    };

    return this.apiService
      .postSecurityLogon(req, {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map((res: HttpResponse<void>) => {
          if (this.isAuthHeaderAdopted()) {
            this.saveRequestTokenFromAuthHeader(res && res.headers);
          }
        })
      );
  }

  /**
   * restituisce i dati dell'user
   */
  getUserData(): Observable<UserDataDTO> {
    return this.apiService
      .getSecurityUserData()
      .pipe(map(res => this.sanitizeUserdata(res)));
  }

  /**
   * applica agli headers passati in argomento i parametri necessari
   */
  ensureHttpHeaders(url: string, httpHeaders: HttpHeaders): HttpHeaders {
    if (this.isAuthHeaderAdopted() && this.doesUrlNeedAuthHeader(url)) {
      httpHeaders = this.setAuthHeader(httpHeaders);
    }

    return httpHeaders;
  }

  /**
   * stabilisce se l'url è fuori l'area di autenticazione
   */
  isUrlOutOfAuth(url: string): boolean {
    const list = this.appService.getConfig().security.urlOutOfAuth || [];

    return list.findIndex(x => url.indexOf(x) > -1) > -1;
  }

  /**
   * imposta l'header di autenticazione
   */
  private setAuthHeader(httpHeaders: HttpHeaders): HttpHeaders {
    if (!(httpHeaders instanceof HttpHeaders)) {
      return httpHeaders;
    }

    const requestToken = this.getRequestToken();

    if (!!requestToken) {
      httpHeaders = httpHeaders.set(AUTH_HEADER_NAME, requestToken);
    }

    return httpHeaders;
  }

  /**
   * salva il token contenuto nell' header di autenticazione
   */
  private saveRequestTokenFromAuthHeader(httpHeaders: HttpHeaders): void {
    if (!(httpHeaders instanceof HttpHeaders)) {
      return;
    }

    const requestToken = httpHeaders.get(AUTH_HEADER_NAME);
    this.saveRequestToken(requestToken);
  }

  /**
   * restituisce il token salvato su local storage
   */
  private getRequestToken(): string {
    return localStorage.getItem(AUTH_STORAGE_ITEM_NAME);
  }

  /**
   * imposta il request tokrn su local storage
   */
  private saveRequestToken(requestToken: string): void {
    if (!requestToken) {
      localStorage.removeItem(AUTH_STORAGE_ITEM_NAME);
    } else {
      localStorage.setItem(AUTH_STORAGE_ITEM_NAME, requestToken);
    }
  }

  /**
   * Determina se utilizzare il sistema di autenticazione
   * tramite header qinetic_authorization e request token
   */
  private isAuthHeaderAdopted(): boolean {
    const demoMode = this.appService.isDemoActive();
    const devMode = this.appService.isDevMode();
    const hostEnv = this.appService.getHostEnv();

    return (
      !demoMode && devMode && (hostEnv === 'localhost' || hostEnv === 'develop')
    );
  }

  /**
   * stabilisce se l'url necessita dell'auth header
   */
  private doesUrlNeedAuthHeader(url: string): boolean {
    return !this.isUrlOutOfAuth(url);
  }

  /**
   * controlla e sanifica i dati dalla userdata
   */
  private sanitizeUserdata(data: UserDataDTO): UserDataDTO {
    if (!data || !data.attributes) {
      throw new Error(
        `I dati dell'utente non sono stati definiti correttamente`
      );
    }

    const positionId = this.getSafeId(
      data.attributes.positionId || data.attributes['position-id']
    );

    if (!positionId) {
      throw new Error(`Impossibile stabilire il numero di pratica`);
    }

    const operation = this.getSafeId(data.attributes.operation);

    if (!operation) {
      throw new Error(`Impossibile stabilire il codice operatività`);
    }

    data.attributes.positionId = positionId;
    data.attributes.operation = operation;

    return data;
  }

  /**
   * restituisce id sanificato
   */
  private getSafeId(id: string): string {
    if (typeof id !== 'string') {
      return null;
    }

    return String(id).trim();
  }
}
